import * as React from "react";
import { useCallback, useEffect, useContext, useRef, useMemo } from "react";
import { Field } from "formik";
import moment from "moment";
import styled from "styled-components";
import isEmpty from "lodash/isEmpty";
import { useIsFetching } from "@tanstack/react-query";
import WidgetEditApplyAllChangesContext from "views/components/contexts/WidgetEditApplyAllChangesContext";
import StreamsContext from "contexts/StreamsContext";
import { createElasticsearchQueryString } from "views/logic/queries/Query";
import { SEARCH_TYPE_RANGE_LIMIT_ERROR_TYPE, DEFAULT_TIMERANGE } from "views/Constants";
import WidgetContext from "views/components/contexts/WidgetContext";
import { PropagateDisableSubmissionState } from "views/components/aggregationwizard";
import ViewsQueryValidation from "views/components/searchbar/queryvalidation/ViewsQueryValidation";
import FormWarningsContext from "contexts/FormWarningsContext";
import FormWarningsProvider from "contexts/FormWarningsProvider";
import useParameters from "views/hooks/useParameters";
import debounceWithPromise from "views/logic/debounceWithPromise";
import validateQuery from "views/components/searchbar/queryvalidation/validateQuery";
import ValidateOnParameterChange from "views/components/searchbar/ValidateOnParameterChange";
import {
  executeDashboardWidgetSubmitHandler as executePluggableSubmitHandler,
  useInitialDashboardWidgetValues as usePluggableInitialValues,
  pluggableValidationPayload
} from "views/logic/searchbar/pluggableSearchBarControlsHandler";
import usePluginEntities from "hooks/usePluginEntities";
import useUserDateTime from "hooks/useUserDateTime";
import { TimeRangeRow, SearchQueryRow, searchBarContainerStyles } from "views/components/searchbar/SearchBarLayout";
import PluggableCommands from "views/components/searchbar/queryinput/PluggableCommands";
import useGlobalOverride from "views/hooks/useGlobalOverride";
import { updateWidget } from "views/logic/slices/widgetActions";
import { setGlobalOverrideQuery, setGlobalOverrideTimerange } from "views/logic/slices/searchExecutionSlice";
import useViewsDispatch from "views/stores/useViewsDispatch";
import useHandlerContext from "views/components/useHandlerContext";
import useView from "views/hooks/useView";
import { normalizeFromSearchBarForBackend } from "views/logic/queries/NormalizeTimeRange";
import QueryHistoryButton from "views/components/searchbar/QueryHistoryButton";
import useSearchConfiguration from "hooks/useSearchConfiguration";
import { defaultCompare } from "logic/DefaultCompare";
import { executeActiveQuery } from "views/logic/slices/viewSlice";
import useSearchResultTimeRangeErrorCheck from "views/hooks/useSearchResultTimeRangeErrorCheck";
import TimeRangeOverrideInfo from "./searchbar/WidgetTimeRangeOverride";
import TimeRangeFilter from "./searchbar/time-range-filter";
import StreamsFilter from "./searchbar/StreamsFilter";
import SearchButton from "./searchbar/SearchButton";
import ViewsQueryInput from "./searchbar/ViewsQueryInput";
import SearchBarForm from "./searchbar/SearchBarForm";
import WidgetQueryOverride from "./WidgetQueryOverride";
import PluggableSearchBarControls from "./searchbar/PluggableSearchBarControls";
const SearchBarContainer = styled.div`
  ${searchBarContainerStyles}
`;
const SearchInputAndValidation = styled.div`
  display: flex;
  flex: 1;
`;
export const updateWidgetSearchControls = (widget, { timerange, streamsAndCategories, queryString }) => widget.toBuilder().timerange(timerange).query(createElasticsearchQueryString(queryString)).streams(streamsAndCategories == null ? void 0 : streamsAndCategories.streams).streamCategories(streamsAndCategories == null ? void 0 : streamsAndCategories.categories).build();
const onSubmit = async (dispatch, values, pluggableSearchBarControls, widget) => {
  const { timerange, streamsAndCategories, queryString } = values;
  const widgetWithPluginData = await executePluggableSubmitHandler(
    dispatch,
    values,
    pluggableSearchBarControls,
    widget
  );
  const newWidget = updateWidgetSearchControls(widgetWithPluginData, {
    timerange,
    streamsAndCategories,
    queryString
  });
  if (!widget.equals(newWidget)) {
    return dispatch(updateWidget(widget.id, newWidget));
  }
  return dispatch(executeActiveQuery());
};
const resetTimeRangeOverride = (dispatch) => dispatch(setGlobalOverrideTimerange(void 0)).then(() => dispatch(executeActiveQuery()));
const resetQueryOverride = (dispatch) => dispatch(setGlobalOverrideQuery(void 0)).then(() => dispatch(executeActiveQuery()));
const useBindApplySearchControlsChanges = (formRef) => {
  const { bindApplySearchControlsChanges } = useContext(WidgetEditApplyAllChangesContext);
  const { userTimezone } = useUserDateTime();
  useEffect(() => {
    bindApplySearchControlsChanges((newWidget) => {
      if (formRef.current) {
        const {
          dirty,
          values: { timerange, ...rest },
          isValid
        } = formRef.current;
        if (dirty && isValid) {
          const normalizedFormValues = {
            timerange: normalizeFromSearchBarForBackend(timerange, userTimezone),
            ...rest
          };
          return updateWidgetSearchControls(newWidget, normalizedFormValues);
        }
      }
      return void 0;
    });
  }, [formRef, bindApplySearchControlsChanges, userTimezone]);
};
const useInitialFormValues = (widget) => {
  var _a, _b;
  const { streams, streamCategories } = widget;
  const timerange = (_a = widget.timerange) != null ? _a : DEFAULT_TIMERANGE;
  const { query_string: queryString } = (_b = widget.query) != null ? _b : createElasticsearchQueryString("");
  const initialValuesFromPlugins = usePluggableInitialValues(widget);
  return useMemo(
    () => ({
      timerange,
      streamsAndCategories: { streams, categories: streamCategories },
      queryString,
      ...initialValuesFromPlugins
    }),
    [timerange, streams, streamCategories, queryString, initialValuesFromPlugins]
  );
};
const debouncedValidateQuery = debounceWithPromise(validateQuery, 350);
const _validateQueryString = (values, globalOverride, pluggableSearchBarControls, userTimezone, context) => {
  var _a, _b;
  const request = {
    queryString: values == null ? void 0 : values.queryString,
    timeRange: !isEmpty(globalOverride == null ? void 0 : globalOverride.timerange) ? globalOverride.timerange : values == null ? void 0 : values.timerange,
    filter: (globalOverride == null ? void 0 : globalOverride.query) ? globalOverride.query : void 0,
    streams: (_a = values == null ? void 0 : values.streamsAndCategories) == null ? void 0 : _a.streams,
    streamCategories: (_b = values == null ? void 0 : values.streamsAndCategories) == null ? void 0 : _b.categories,
    ...pluggableValidationPayload(values, context, pluggableSearchBarControls)
  };
  return debouncedValidateQuery(request, userTimezone);
};
const WidgetQueryControls = () => {
  var _a, _b;
  const editorRef = useRef(null);
  const view = useView();
  const streamsContext = useContext(StreamsContext);
  const availableStreams = useMemo(() => streamsContext != null ? streamsContext : [], [streamsContext]);
  const globalOverride = useGlobalOverride();
  const widget = useContext(WidgetContext);
  const { userTimezone } = useUserDateTime();
  const { config } = useSearchConfiguration();
  const isValidatingQuery = !!useIsFetching({
    queryKey: ["validateSearchQuery"]
  });
  const pluggableSearchBarControls = usePluginEntities("views.components.searchBar");
  const limitDuration = (_a = moment.duration(config == null ? void 0 : config.query_time_range_limit).asSeconds()) != null ? _a : 0;
  const hasTimeRangeOverride = (globalOverride == null ? void 0 : globalOverride.timerange) !== void 0;
  const hasQueryOverride = !!((_b = globalOverride == null ? void 0 : globalOverride.query) == null ? void 0 : _b.query_string);
  const formRef = useRef(null);
  const { parameters } = useParameters();
  const handlerContext = useHandlerContext();
  const validate = useCallback(
    (values) => _validateQueryString(values, globalOverride, pluggableSearchBarControls, userTimezone, handlerContext),
    [globalOverride, pluggableSearchBarControls, userTimezone, handlerContext]
  );
  const initialValues = useInitialFormValues(widget);
  const dispatch = useViewsDispatch();
  const _onSubmit = useCallback(
    (values) => onSubmit(dispatch, values, pluggableSearchBarControls, widget),
    [dispatch, pluggableSearchBarControls, widget]
  );
  const _resetTimeRangeOverride = useCallback(() => dispatch(resetTimeRangeOverride), [dispatch]);
  const _resetQueryOverride = useCallback(() => dispatch(resetQueryOverride), [dispatch]);
  const allStreams = useMemo(
    () => availableStreams.map((stream) => ({
      key: stream.title,
      value: stream.id
    })),
    [availableStreams]
  );
  const availableStreamCategories = useMemo(
    () => availableStreams.reduce((acc, stream) => {
      var _a2;
      (_a2 = stream.categories) == null ? void 0 : _a2.forEach((category) => {
        if (!acc.find((option) => option.value === category))
          acc.push({ key: category, value: category });
      });
      return acc;
    }, []).sort((a, b) => defaultCompare(a.value, b.value)),
    [availableStreams]
  );
  useBindApplySearchControlsChanges(formRef);
  const searchResultTimeRangeErrorCheck = useSearchResultTimeRangeErrorCheck(SEARCH_TYPE_RANGE_LIMIT_ERROR_TYPE);
  return /* @__PURE__ */ React.createElement(FormWarningsProvider, null, /* @__PURE__ */ React.createElement(
    SearchBarForm,
    {
      initialValues,
      limitDuration,
      formRef,
      onSubmit: _onSubmit,
      validateQueryString: validate
    },
    ({ dirty, errors, isValid, isSubmitting, handleSubmit, values, setFieldValue, validateForm }) => {
      const showTimeRangeErrorFromResults = searchResultTimeRangeErrorCheck(values == null ? void 0 : values.timerange);
      const disableSearchSubmit = isSubmitting || isValidatingQuery || !isValid || showTimeRangeErrorFromResults;
      return /* @__PURE__ */ React.createElement(SearchBarContainer, null, /* @__PURE__ */ React.createElement(
        PropagateDisableSubmissionState,
        {
          formKey: "widget-query-controls",
          disableSubmission: disableSearchSubmit
        }
      ), /* @__PURE__ */ React.createElement(ValidateOnParameterChange, { parameters }), /* @__PURE__ */ React.createElement(TimeRangeRow, null, !hasTimeRangeOverride && /* @__PURE__ */ React.createElement(
        TimeRangeFilter,
        {
          disabled: hasTimeRangeOverride,
          limitDuration,
          onChange: (nextTimeRange) => setFieldValue("timerange", nextTimeRange),
          value: values == null ? void 0 : values.timerange,
          hasErrorOnMount: !!errors.timerange || showTimeRangeErrorFromResults
        }
      ), hasTimeRangeOverride && /* @__PURE__ */ React.createElement(TimeRangeOverrideInfo, { value: globalOverride == null ? void 0 : globalOverride.timerange, onReset: _resetTimeRangeOverride }), /* @__PURE__ */ React.createElement(Field, { name: "streamsAndCategories" }, ({ field: { name, value, onChange } }) => /* @__PURE__ */ React.createElement(
        StreamsFilter,
        {
          value: { streams: value == null ? void 0 : value.streams, categories: value == null ? void 0 : value.categories },
          streams: allStreams,
          streamCategories: availableStreamCategories,
          onChange: (selected) => {
            onChange({
              target: {
                value: selected,
                name
              }
            });
          }
        }
      ))), /* @__PURE__ */ React.createElement(SearchQueryRow, null, /* @__PURE__ */ React.createElement(SearchButton, { disabled: disableSearchSubmit, dirty, displaySpinner: isSubmitting }), /* @__PURE__ */ React.createElement(SearchInputAndValidation, null, /* @__PURE__ */ React.createElement(Field, { name: "queryString" }, ({ field: { name, value, onChange }, meta: { error } }) => /* @__PURE__ */ React.createElement(FormWarningsContext.Consumer, null, ({ warnings }) => /* @__PURE__ */ React.createElement(PluggableCommands, { usage: "widget_query" }, (customCommands) => {
        var _a2;
        return /* @__PURE__ */ React.createElement(
          ViewsQueryInput,
          {
            value,
            view,
            timeRange: !isEmpty(globalOverride == null ? void 0 : globalOverride.timerange) ? globalOverride.timerange : values == null ? void 0 : values.timerange,
            streams: (_a2 = values == null ? void 0 : values.streamsAndCategories) == null ? void 0 : _a2.streams,
            placeholder: 'Type your search query here and press enter. E.g.: ("not found" AND http) OR http_response_code:[400 TO 404]',
            error,
            ref: editorRef,
            disableExecution: disableSearchSubmit,
            isValidating: isValidatingQuery,
            warning: warnings.queryString,
            validate: validateForm,
            name,
            onChange,
            onExecute: handleSubmit,
            commands: customCommands
          }
        );
      }))), /* @__PURE__ */ React.createElement(ViewsQueryValidation, null), /* @__PURE__ */ React.createElement(QueryHistoryButton, { editorRef })), hasQueryOverride && /* @__PURE__ */ React.createElement(WidgetQueryOverride, { value: globalOverride == null ? void 0 : globalOverride.query, onReset: _resetQueryOverride })), /* @__PURE__ */ React.createElement(PluggableSearchBarControls, null));
    }
  ));
};
export default WidgetQueryControls;
